<template>
  <div class="" v-if="isPropertyLoading">
    <Loading />
  </div>

  <div v-else>
    <BethsLayout :layoutTitle="`Login - ${property?.name}`">
      <div class="flex flex-col justify-center min-h-600">
        <!-- component -->
        <div
          class="m-auto my-10 border-2 border-gray-300 shadow-md rounded-md w-12/12 lg:w-6/12 md:w-8/12 sm:10/12"
        >
          <div class="px-8 py-8 rounded-xl">
            <h1
              id="login-title"
              name="login-title"
              class="mt-2 flex flex-wrap content-center font-bold text-2xl"
            >
              <div class="font-normal">Login to &nbsp;</div>
              <div>
                {{ property?.name }}
              </div>
            </h1>

            <div class="mt-4 mb-5" v-if="property?.isPasswordLoginEnabled ?? true">
              <p class="font-light text-base text-gray-400">
                Don't have an account?
                <a
                  @click="registerClick"
                  class="font-bold underline text-lg text-blue-500 cursor-pointer"
                  data-cy="RegisterPageLink"
                >
                  Register
                </a>
              </p>
            </div>

            <Loading v-if="isLoggingIn" />
            <ErrorDisplay
              :label="loginErrorLabel"
              id="login-error"
              :message="loginError"
              :message-is-html="true"
              :isVisible="loginError != null"
            />
            <InfoDisplay
              label="An Account with this email has already been created. Please login with your password or click the 'Forgot Password?' button."
              :isVisible="isRedirectedFromRegister"
            />

            <form action="" class="mt-8" v-if="property?.isPasswordLoginEnabled ?? true">
              <div class="my-5 text-sm">
                <label for="username" id="username_label" class="block text-black">
                  Username
                </label>
                <input
                  id="username"
                  v-model="email"
                  type="text"
                  autofocus
                  class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:outline-double"
                  placeholder="caleb@mycompany.com"
                  autocomplete="username"
                />
              </div>
              <div class="my-5 text-sm">
                <label for="password" class="block text-black">Password</label>
                <input
                  id="password"
                  v-model="password"
                  type="password"
                  autocomplete="current-password"
                  class="w-full px-4 py-3 mt-2 text-xl font-mono bg-gray-200 border border-gray-400 rounded-sm focus:outline-double"
                  placeholder="Password"
                />
                <div class="flex justify-end mt-2 text-xs text-gray-600">
                  <router-link
                    class="duration-100 hover:text-black"
                    id="reset-password"
                    title="reset-password"
                    :to="`/${property?.siteCode}/forgotpassword`"
                  >
                    Forgot Password?
                  </router-link>
                </div>
              </div>
              <div class="flex flex-wrap justify-between flex-row-reverse mt-8">
                <button
                  id="login_button"
                  class="w-40 flex py-3 px-3 text-center text-white duration-300 bg-btn hover:bg-blue-500 rounded"
                  @click.prevent="loginWithEmail"
                >
                  <span class="i-icon i-icon-door-handle">
                    <svg viewBox="0 0 64 64" fill="none" width="28px" height="28px">
                      <rect
                        x="6"
                        y="4"
                        width="26"
                        height="40"
                        rx="2"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        style="width: 48px; height: 55px"
                      ></rect>
                      <path
                        d="M14 34H24"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                      <path
                        d="M42 20V14H23.0004C22.0882 12.7856 20.6358 12 19 12C16.2386 12 14 14.2386 14 17C14 19.7614 16.2386 22 19 22C20.6358 22 22.0882 21.2144 23.0004 20H42Z"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </svg>
                  </span>
                  <span class="flex-grow text-xl pr-5">Login</span>
                </button>
              </div>
            </form>

            <div
              v-if="hasExternalLogin && property?.isPasswordLoginEnabled"
              class="flex items-center justify-center mt-10 md:justify-between"
            >
              <div style="height: 1px" class="hidden w-4/12 bg-gray-300 md:block"></div>
              <p class="text-sm font-light text-gray-400 md:mx-2">Login With External Account</p>
              <div style="height: 1px" class="hidden w-4/12 bg-gray-300 md:block"></div>
            </div>

            <div v-if="hasExternalLogin" class="mt-7">
              <!-- <form
                id="external-account"
                method="post"
                action="/Identity/Account/ExternalLogin?returnUrl=%2F150cal"
              > -->
              <!-- <div>
                  <button
                    name="provider"
                    value="google"
                    type="submit"
                    class="w-full p-3 text-center text-white duration-300 bg-blue-900 rounded-sm hover:bg-blue-700"
                  >
                    <icon-google />
                    Google
                  </button>
                </div> -->
              <div v-if="property.isSSOEnabled ?? false" class="text-center">
                <a
                  :href="externalProviderUrl"
                  name="provider"
                  value="okta"
                  type="submit"
                  class="w-full p-4 text-center text-white duration-300 bg-blue-400 rounded-sm hover:bg-blue-500"
                >
                  <!-- <icon-okta /> -->
                  {{ externalProviderName }}
                </a>
              </div>

              <div v-if="property.isAzureAdEnabled || false" class="text-center">
                <a
                  :href="
                    `/azuread/${property.siteCode}` + (returnUrl ? '?returnUrl=' + returnUrl : '')
                  "
                  name="provider"
                  value="okta"
                  type="submit"
                  class="w-full p-4 text-center text-white duration-300 bg-blue-400 rounded-sm hover:bg-blue-500"
                >
                  <span v-if="externalProviderName">{{ externalProviderName }}</span>
                  <span v-else>Microsoft Account</span>
                </a>
              </div>
              <!-- </form> -->
            </div>
          </div>
        </div>
      </div>
    </BethsLayout>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from "../store/userStore";
import { useBldgStore } from "../store/bldgStore";
import { computed, ref, onMounted, onActivated, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { isPortalHomeUrl } from "../helpers";


const userStore = useUserStore();
const bldgStore = useBldgStore();
const router = useRouter();
const route = useRoute();

var isPropertyLoading = computed(() => bldgStore.isLoading);
let isLoading = ref(false);
var property = computed(() => bldgStore.building);
let isLoggingIn = ref(false);

let hasExternalLogin = computed(
  () => (property.value?.isSSOEnabled ?? false) || (property.value?.isAzureAdEnabled ?? false)
);
var externalProviderName = computed(() => property.value?.ssoButtonLabel ?? false);
var externalProviderUrl = computed(() => property.value?.ssoLoginUrl ?? "");

onMounted(loadData);
onActivated(() => loadData);
watch(property, loadData);

var returnUrl = ref<string | null>(null);

async function loadData() {
  isLoading.value = true;
  if (property?.value?.portalId) {
    // The only supported external login provider is OKTA at the moment.
    //hasExternalLogin.value = property.value?.isOktaLoginEnabled ?? false;
    //console.log("Login> has external Login:" + hasExternalLogin.value);

    if (route.query["user"]) {
      console.log("user param exists");
      email.value = route.query?.user?.toString();
      isRedirectedFromRegister.value = true;
    }

    await router.isReady();
    returnUrl.value =
      router.currentRoute.value?.query["returnUrl"]?.toString() ||
      router.currentRoute.value?.hash?.substring(1);

    if (returnUrl.value) console.log("return Url found: " + returnUrl.value);

    let forcePasswordLogin = route.query["password-login"]?.toString();
    if (forcePasswordLogin) {
      property.value.isPasswordLoginEnabled = true;
    }
  }
  isLoading.value = false;
}

let loginError = ref<string | null>(null);
let loginErrorLabel = ref<string | null>(null);
let isRedirectedFromRegister = ref(false);
let email = ref("");
let password = ref("");

async function loginWithEmail() {
  console.log(`Attempting login for: ${email.value}`);

  if (email.value === "" || password.value === "") {
    loginErrorLabel.value = "Enter your login info";
    loginError.value = "Please specify both a username and password";
    return;
  }

  isLoggingIn.value = true;
  loginErrorLabel.value = "";
  loginError.value = "";

  let loginResult = await userStore.loginUser(
    email.value.trim(),
    password.value,
    property.value.portalId,
    null,
    null,
    property.value.siteCode
  );

  if (loginResult && !loginResult.error) {
    if (loginResult.upgradePassword) {
      router.push(
        `/${property.value.siteCode}/setpassword?user=${encodeURIComponent(email.value)}&oldpass=${
          password.value
        }`
      );
    } else if (userStore.isLoggedIn) {
      var user = await userStore.getUser(bldgStore.siteCode, bldgStore.property.portalId, true);
      if (user.token) {
        // One call covers every API client - the token is shared in ClientBase.
        userStore.setAuthToken(user.token);
      }

      // Only auto-launch VR when landing on Home (no returnUrl, or returnUrl is Home itself)
      userStore.pendingVrAutoLaunch = isPortalHomeUrl(returnUrl.value, property.value.siteCode);
      if (returnUrl.value) {
        //&& returnUrl.value.startsWith("/" + property.value.siteCode)) {
        router.replace(returnUrl.value);
      } else {
        router.replace("/" + property.value.siteCode);
      }
    }
  } else {
    console.log("loginResult:", loginResult);
    if (loginResult?.error === '"User not found"') {
      loginErrorLabel.value = "User not found";
      loginError.value =
        "An account with this username has not been set up at this portal.<div class='mt-4'>Click the 'register' link above to create one.</div>";
    } else {
      loginErrorLabel.value = "Couldn't log in";
      loginError.value = loginResult?.error ?? "Invalid Login";
    }
    isLoggingIn.value = false;
  }
}

function registerClick() {
  let path = `/${property.value.siteCode}/register`;
  let query: any = { user: email.value };
  if (returnUrl.value) query.returnUrl = returnUrl.value;
  router.replace({ path, query });
}
</script>
